import Markdown from "react-markdown";
import { ToolInvocation } from "../../../toolsComponents/ToolInvocation";

// Define strict types for message parts
type MessagePartType = 'text' | 'source' | 'reasoning' | 'tool-invocation' | 'step-start' | 'file';

interface BaseMessagePart {
  type: MessagePartType;
}

interface TextPart extends BaseMessagePart {
  type: 'text';
  text: string;
}

interface SourcePart extends BaseMessagePart {
  type: 'source';
  source: { url: string };
}

interface ReasoningPart extends BaseMessagePart {
  type: 'reasoning';
  reasoning: string;
}

interface ToolInvocationPart extends BaseMessagePart {
  type: 'tool-invocation';
  toolInvocation: Record<string, unknown>;
}

interface StepStartPart extends BaseMessagePart {
  type: 'step-start';
}

interface FilePart extends BaseMessagePart {
  type: 'file';
  mimeType: string;
  url: string;
  filename: string;
}

type MessagePart = TextPart | SourcePart | ReasoningPart | ToolInvocationPart | StepStartPart | FilePart;

// Constants for height calculations
const LINE_HEIGHT = 20;
const CHARS_PER_LINE = 60;
const BASE_PADDING = 24;
const CODE_BLOCK_PADDING = 24;

// Helper function to estimate markdown content height
const estimateMarkdownHeight = (text: string) => {
  const lines = text.split('\n');
  let estimatedHeight = 0;

  for (const line of lines) {
    // Code blocks (indented or fenced)
    if (line.startsWith('```') || line.startsWith('    ')) {
      estimatedHeight += CODE_BLOCK_PADDING; // Extra padding for code block
      continue;
    }

    // Headers
    if (line.startsWith('#')) {
      estimatedHeight += LINE_HEIGHT * 2; // Headers need more space
      continue;
    }

    // Lists
    if (line.match(/^[-*+]\s|^\d+\.\s/)) {
      estimatedHeight += LINE_HEIGHT * 1.2; // List items need slight extra space
      continue;
    }

    // Tables (rough estimation)
    if (line.includes('|')) {
      estimatedHeight += LINE_HEIGHT * 1.5;
      continue;
    }

    // Regular text
    if (line.trim().length > 0) {
      estimatedHeight += Math.ceil(line.length / CHARS_PER_LINE) * LINE_HEIGHT;
    } else {
      // Empty lines for paragraph breaks
      estimatedHeight += LINE_HEIGHT * 0.5;
    }
  }

  return Math.max(BASE_PADDING, estimatedHeight);
};

// Update the MESSAGE_PART_HEIGHTS with new markdown-aware calculation
const MESSAGE_PART_HEIGHTS = {
  text: (part: TextPart) => estimateMarkdownHeight(part.text),
  
  source: () => 30,
  
  reasoning: (part: ReasoningPart) => 
    estimateMarkdownHeight(part.reasoning) + 32, // Additional padding for reasoning box
  
  'tool-invocation': (part: ToolInvocationPart) => {
    const baseHeight = 120;
    const jsonString = JSON.stringify(part.toolInvocation, null, 2);
    const lineCount = jsonString.split('\n').length;
    return baseHeight + (lineCount * 20);
  },
  
  'step-start': () => 28,

  file: () => 80, // Fixed height for file messages
} as const;

// Type-safe height calculator
export const calculatePartHeight = (part: MessagePart | null): number => {
  if (!part) return 0;
  
  const calculator = MESSAGE_PART_HEIGHTS[part.type];
  if (!calculator) {
    console.warn(`Unknown message part type: ${part.type}`);
    return BASE_PADDING;
  }
  
  const height = calculator(part as any);
  console.debug(`Height calculation for ${part.type}:`, {
    part,
    calculatedHeight: height,
    baseHeight: BASE_PADDING
  });
  
  return height;
};

// Update the props interface to use the new types
interface MessagePartProps {
  part: MessagePart;
  role: "user" | "assistant" | "tool" | "system" | "data";
  index: number;
  onSendMessage?: (text: string) => void;
  sessionId?: string;
  carrierId?: string;
}

export const MessagePart = ({ part, role, index, onSendMessage, sessionId, carrierId }: MessagePartProps) => {
  if (!part) return null;

  switch (part.type) {
    case "text":
      return (
        <div className={`${role === "user" ? "text-white" : "text-gray-900"} prose prose-sm max-w-none whitespace-pre-wrap [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&>p]:my-0.5 [&>ul]:my-0.5 [&>ol]:my-0.5 [&>li]:my-0.5`}>
          <Markdown components={{
            // Prevent XSS by opening links in new tab
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
            // Add syntax highlighting for code blocks
            code: ({ node, ...props }) => (
              <code {...props} className="bg-gray-100 rounded px-1 py-0.5" />
            ),
            // Reduce spacing for paragraphs
            p: ({ node, ...props }) => (
              <p {...props} className="my-0 leading-tight" />
            ),
            // Reduce spacing for lists
            ul: ({ node, ...props }) => (
              <ul {...props} className="my-0 leading-tight list-disc pl-4" />
            ),
            ol: ({ node, ...props }) => (
              <ol {...props} className="my-0 leading-tight list-decimal pl-4" />
            ),
            li: ({ node, ...props }) => (
              <li {...props} className="my-0 leading-tight pl-1 marker:align-middle marker:leading-none" />
            ),
          }}>
            {part.text.replace(/\[carrier:[^\]]+\]\s*/g, "")}
          </Markdown>
        </div>
      );

    case "source":
      return (
        <div className="text-blue-600 hover:underline">
          <a href={part.source?.url} target="_blank" rel="noopener noreferrer">
            {part.source?.url}
          </a>
        </div>
      );

    case "reasoning":
      return (
        <div className="text-gray-600 italic bg-gray-50 p-1 rounded my-1">
          {part.reasoning}
        </div>
      );

    case "tool-invocation":
      return (
        <ToolInvocation
          toolInvocation={part.toolInvocation}
          onSendMessage={onSendMessage}
          sessionId={sessionId || ""}
          carrierId={carrierId || ""}
        />
      );

    case "file":
      return (
        <div className="flex items-center gap-1 p-1 bg-gray-50 rounded-lg">
          <span className="text-2xl">
            {part.mimeType.includes('pdf') ? '📄' :
             part.mimeType.includes('image') ? '🖼️' :
             part.mimeType.includes('word') ? '📝' :
             part.mimeType.includes('excel') ? '📊' : '📎'}
          </span>
          <div className="flex-1">
            <p className="text-sm font-medium">{part.filename}</p>
          </div>
          <a 
            href={part.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 text-sm"
            onClick={(e) => {
              // If the URL is our proxy route, let it handle the download
              if (part.url.startsWith('/api/chat-files/')) {
                return;
              }
              // For any other URLs, prevent default and handle differently if needed
              e.preventDefault();
            }}
          >
            Ver
          </a>
        </div>
      );

    case "step-start":
      return index > 0 ? (
        <div key={index} className="text-gray-500">
          <hr className="my-1 border-gray-300" />
        </div>
      ) : null;

    default:
      // Unknown part types (e.g. tool-call, tool-result during v6 streaming)
      // are transient — silently ignore them
      return null;
  }
};
